import { FC, useContext, useState, ReactNode, Dispatch, SetStateAction } from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { useRelayEnvironment, useFragment, graphql } from 'react-relay';

import { StepHeader } from 'dibs-elements/exports/StepHeader';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import InputsWrapper from './sharedComponents/InputsWrapper';

import UserAgreement from './sharedComponents/UserAgreement';
import FirstName from './sharedComponents/FirstName';
import LastName from './sharedComponents/LastName';
import Email from './sharedComponents/Email';
import PhoneNumber from './sharedComponents/PhoneNumber';
import State from './sharedComponents/State';
import ZipCode from './sharedComponents/ZipCode';
import Country from './sharedComponents/Country';
import City from './sharedComponents/City';
import ExperienceSellingOnline from './sharedComponents/ExperienceSellingOnline';
import StreetAddressAutocomplete, {
    AddressDetails,
} from './sharedComponents/StreetAddressAutocomplete';
import FormButton from './sharedComponents/FormButton';

import SellerFormContext from './SellerFormContext';
import { sellerApplicationFormFieldsValidation } from '../helpers/sellerApplicationFormValidator';

import { submitSellerApplicationLeadMutation } from './mutations/submitSellerApplicationLead';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking';
import { shouldShowStateField } from '../helpers/common';

import { ContactDetailsKeys } from './SellerApplicationFormReducer';
import { SellerApplicationFormConsignorsDetails_viewer$key } from './__generated__/SellerApplicationFormConsignorsDetails_viewer.graphql';

import dibsCss from 'dibs-css';

const messages = defineMessages({
    title: {
        id: 'SellerApplicationForm.ConsignorsDetails.title',
        defaultMessage: 'Contact Information',
    },
});

type ErrorType = { [fieldName in ContactDetailsKeys]?: ReactNode };

const viewerFragment = graphql`
    fragment SellerApplicationFormConsignorsDetails_viewer on Viewer {
        geographySalesforce(contextName: "SELLER_APPLICATION") {
            ...State_geographySalesforce
            ...Country_geographySalesforce
        }
    }
`;

type Props = {
    viewer: SellerApplicationFormConsignorsDetails_viewer$key;
    setFormError: Dispatch<SetStateAction<boolean>>;
};

const SellerApplicationFormConsignorsDetails: FC<Props> = ({ viewer: viewerRef, setFormError }) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const { state, dispatch } = useContext(SellerFormContext);
    const viewer = useFragment(viewerFragment, viewerRef);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState<ErrorType>({});

    const {
        firstName,
        lastName,
        phoneNumber,
        email,
        country,
        line1,
        zipCode,
        city,
        state: countryState,
        experienceSellingOnline,
    } = state.formDataContactDetails;
    const showStateField = shouldShowStateField(country);
    const geographySalesforce = viewer?.geographySalesforce;

    const validateField = (name: ContactDetailsKeys, value: string | boolean): void => {
        const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
        const error = errors?.[name];

        setErrorMessages({
            ...errorMessages,
            [name]: error,
        });
    };

    const setFormField = (field: ContactDetailsKeys, value: string | boolean): void => {
        if (field === 'country' && value !== 'US') {
            dispatch({ type: 'SET_FORM_CONTACT_DETAILS', formDataContactDetails: { state: '' } });
        }

        dispatch({ type: 'SET_FORM_CONTACT_DETAILS', formDataContactDetails: { [field]: value } });
    };

    const setField = (name: ContactDetailsKeys, value: string | boolean): void => {
        setFormField(name, value);
        validateField(name, value);
    };

    async function submit(): Promise<void> {
        const { isValid, errors } = sellerApplicationFormFieldsValidation({
            firstName,
            lastName,
            phoneNumber,
            email,
            country,
            line1,
            zipCode,
            city,
            experienceSellingOnline,
            ...(showStateField && { state: countryState }),
        });
        if (isValid) {
            setIsLoading(true);
            setErrorMessages({});
            try {
                const captchaAction = 'SELLER_APPLICATION';

                const { captchaKey, captchaToken } = await executeCaptcha({
                    action: captchaAction,
                    featureFlag: 'sellerApplicationCaptcha',
                    featureFlagV3: 'sellerApplicationCaptcha',
                    relayEnvironment: environment,
                });

                const response = await submitSellerApplicationLeadMutation(environment, {
                    company: `${firstName} ${lastName} company`,
                    captchaAction,
                    captchaKey,
                    captchaToken,
                    firstName,
                    lastName,
                    phone: phoneNumber,
                    email,
                    country,
                    street: line1,
                    postalCode: zipCode,
                    city,
                    hasExperienceSellingOnline: experienceSellingOnline,
                    isConsignorsLead: true,
                    ...(showStateField && { state: countryState }),
                });

                if (!response?.SubmitSellerApplicationLead?.pass) {
                    throw Error('Google captcha validation error');
                }

                const leadId = response?.SubmitSellerApplicationLead?.leadId;

                trackSellerApplicationEvent('individual', 'submit');
                dispatch({
                    type: 'SET_CREATED_LEAD_ID',
                    leadId: leadId || '',
                });
                dispatch({ type: 'SET_STEP', step: 'CONSIGNORS_STEP_ITEMS' });
            } catch (err) {
                setFormError(true);
            }
            setIsLoading(false);
        } else {
            setIsLoading(false);
            setErrorMessages(errors);
        }
    }

    const onAddressLineAutocompleteDetailsReceived = (addressDetails: AddressDetails): void => {
        const {
            country: addressCountry,
            city: addressCity,
            stateOrRegion: addressState,
            zipCode: addressZipCode,
        } = addressDetails || {};
        setField('country', addressCountry || '');
        setField('city', addressCity || '');
        setField('state', addressState || '');
        setField('zipCode', addressZipCode || '');
    };

    return (
        <div className={dibsCss.mbLarge} data-tn="individual-application-form">
            <StepHeader title={intl.formatMessage(messages.title)} totalSteps={2} currentStep={1} />
            <div className={dibsCss.wFull}>
                <InputsWrapper>
                    <FirstName
                        errorMessage={errorMessages?.firstName}
                        onChange={e => {
                            const value = e.target.value;
                            setField('firstName', value);
                        }}
                        isLoading={isLoading}
                        value={firstName}
                    />
                    <LastName
                        onChange={e => {
                            const value = e.target.value;
                            setField('lastName', value);
                        }}
                        isLoading={isLoading}
                        errorMessage={errorMessages?.lastName}
                        value={lastName}
                    />
                </InputsWrapper>
                <InputsWrapper>
                    <Email
                        isLoading={isLoading}
                        onChange={e => {
                            const value = e.target.value;
                            setField('email', value);
                        }}
                        errorMessage={errorMessages?.email}
                        value={email}
                    />
                    <PhoneNumber
                        isLoading={isLoading}
                        onChange={({ value, isValid }) => {
                            setField('phoneNumber', isValid ? value : '');
                        }}
                        errorMessage={errorMessages?.phoneNumber}
                        value={phoneNumber}
                    />
                </InputsWrapper>
                <div className={dibsCss.mbSmall}>
                    <StreetAddressAutocomplete
                        value={line1}
                        onSelect={value => setField('line1', value)}
                        onChange={e => {
                            const value = e.target.value;
                            setField('line1', value);
                        }}
                        onDetailsReceived={onAddressLineAutocompleteDetailsReceived}
                        disabled={isLoading}
                        errorMessage={errorMessages?.line1}
                    />
                </div>
                <InputsWrapper>
                    <Country
                        geographySalesforce={geographySalesforce || null}
                        onChange={e => {
                            const value = e.target.value;
                            setField('country', value);
                        }}
                        errorMessage={errorMessages?.country}
                        disabled={isLoading}
                        value={country}
                    />
                    <ZipCode
                        onChange={e => {
                            const value = e.target.value;
                            setField('zipCode', value);
                        }}
                        value={zipCode}
                        disabled={isLoading}
                        errorMessage={errorMessages?.zipCode}
                    />
                </InputsWrapper>
                <InputsWrapper>
                    <City
                        onChange={e => {
                            const value = e.target.value;
                            setField('city', value);
                        }}
                        disabled={isLoading}
                        value={city}
                        errorMessage={errorMessages?.city}
                    />
                    {showStateField && (
                        <State
                            onChange={e => {
                                const value = e.target.value;
                                setField('state', value);
                            }}
                            errorMessage={errorMessages?.state}
                            value={countryState}
                            geographySalesforce={geographySalesforce || null}
                            disabled={isLoading}
                        />
                    )}
                </InputsWrapper>
                <div className={dibsCss.mbSmall}>
                    <ExperienceSellingOnline
                        onChange={e => {
                            setField('experienceSellingOnline', e.target.value === 'true');
                        }}
                        disabled={isLoading}
                        errorMessage={errorMessages?.experienceSellingOnline}
                    />
                </div>
                <FormButton isLoading={isLoading} onSubmit={submit} />
                <div className={dibsCss.mbXxlarge}>
                    <UserAgreement />
                </div>
            </div>
        </div>
    );
};

export default SellerApplicationFormConsignorsDetails;
